// ============================================================
// Preview data for the CEO portal.
// Used until REACT_APP_CRM_SUPABASE_URL / _ANON_KEY point at the CRM SaaS
// project and its `ceo-summary` edge function is deployed. Shaped exactly
// like the live response so swapping in real data changes nothing in the UI.
// ============================================================
import { CeoSummary } from './ceoApi';

const YEAR = new Date().getFullYear();

// Plausible mid-size UAE company: ~AED 4.1M revenue, ~AED 2.6M cost.
const MONTHLY = [
  ['Jan', 286000, 191000], ['Feb', 312000, 204000], ['Mar', 358000, 221000],
  ['Apr', 341000, 198000], ['May', 402000, 246000], ['Jun', 377000, 232000],
  ['Jul', 428000, 258000], ['Aug', 361000, 240000], ['Sep', 394000, 227000],
  ['Oct', 412000, 251000], ['Nov', 0, 0], ['Dec', 0, 0],
] as const;

const revenueYtd = MONTHLY.reduce((s, m) => s + m[1], 0);
const expensesYtd = MONTHLY.reduce((s, m) => s + m[2], 0);
const profitYtd = revenueYtd - expensesYtd;

export const CEO_MOCK: CeoSummary = {
  company: {
    id: 'company-preview',
    name: 'Asset Desk Real Estate LLC',
    jurisdiction: 'Dubai, United Arab Emirates',
    licenceNumber: '1145872',
    trn: '100472938400003',
  },
  currency: 'AED',
  year: YEAR,
  revenueYtd,
  expensesYtd,
  profitYtd,
  marginPct: (profitYtd / revenueYtd) * 100,
  receivableTotal: 486300,
  overdueTotal: 172450,
  overdueCount: 4,
  overdue: [
    { customer: 'Meridian Holdings FZ-LLC', amount: 78400, dueDate: daysAgo(41), invoiceNo: 'INV-000214' },
    { customer: 'Al Barsha Facilities Co.', amount: 44100, dueDate: daysAgo(23), invoiceNo: 'INV-000221' },
    { customer: 'Vertex Interiors LLC', amount: 31500, dueDate: daysAgo(12), invoiceNo: 'INV-000229' },
    { customer: 'Sana Trading DMCC', amount: 18450, dueDate: daysAgo(5), invoiceNo: 'INV-000233' },
  ],
  spendYtdInclVat: Math.round(expensesYtd * 1.05),
  vat: {
    periodLabel: `Q${Math.floor(new Date().getMonth() / 3) + 1} ${YEAR}`,
    periodStart: quarterStart(),
    periodEnd: quarterEnd(),
    dueDate: inDays(34),
    outputVat: 58900,
    inputVat: 31240,
    netVat: 27660,
  },
  monthly: MONTHLY.map(([month, revenue, expenses]) => ({ month, revenue, expenses })),
  topCustomers: [
    { name: 'Meridian Holdings FZ-LLC', amount: 742000 },
    { name: 'Emaar Community Management', amount: 588500 },
    { name: 'Al Barsha Facilities Co.', amount: 431000 },
    { name: 'Vertex Interiors LLC', amount: 296400 },
    { name: 'Sana Trading DMCC', amount: 184900 },
  ],
  topSuppliers: [
    { name: 'Gulf Maintenance Services', amount: 512000 },
    { name: 'DEWA', amount: 288300 },
    { name: 'Emirates Facilities Group', amount: 214600 },
    { name: 'Alpha Legal Consultants', amount: 132000 },
    { name: 'Prime Office Supplies', amount: 61400 },
  ],
  compliance: {
    licence: { label: 'Trade licence', date: inDays(72), days: 72 },
    visas: [
      { label: 'Ahmed Khan', date: inDays(18), days: 18 },
      { label: 'Marilyn Santos', date: inDays(63), days: 63 },
    ],
  },
  // Two AED accounts plus one USD, so the preview exercises the case the live
  // data allows: total covers the AED accounts only, the USD one is listed but
  // deliberately not FX-converted into it.
  bank: {
    total: 1148600,
    currency: 'AED',
    asOf: daysAgo(2),
    lastUpdated: `${daysAgo(1)}T09:24:00Z`,
    accounts: [
      { name: 'Emirates NBD · Current', balance: 862400, currency: 'AED', asOf: daysAgo(2), updatedAt: `${daysAgo(1)}T09:24:00Z` },
      { name: 'Mashreq · Call deposit', balance: 286200, currency: 'AED', asOf: daysAgo(4), updatedAt: `${daysAgo(4)}T14:02:00Z` },
      { name: 'Wio · USD', balance: 41500, currency: 'USD', asOf: daysAgo(6), updatedAt: `${daysAgo(6)}T08:15:00Z` },
    ],
  },
  source: 'mock',
};

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function inDays(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return iso(d);
}
function daysAgo(n: number): string {
  return inDays(-n);
}
function quarterStart(): string {
  const d = new Date();
  return iso(new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1));
}
function quarterEnd(): string {
  const d = new Date();
  return iso(new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3 + 3, 0));
}
